import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ScrollFade from './ScrollFade';
import { Activity } from '../types';
import { FloatThresholds, getFloatTier, getFloatColor, getFloatBadgeText } from '../utils/riskUtils';
import ExpandedRow from './ExpandedRow';
import { DemoSignInModal } from './DemoSignInModal';
import { formatDateInput } from '../utils/dateUtils';
import { useFlowClient } from '../hooks/useFlowClient';
import { useDemoGuard } from '../hooks/useDemoGuard';
import { useApp } from '../context/AppContext';

export type SectionFlag = { color: 'red' | 'amber'; label: string } | null;

// Display-only toggle — flag computation (Weekly.tsx's startingFlag/finishingFlag/
// ongoingFlag) stays intact and unchanged; this just controls whether the
// Flags column (header + per-row chip) renders. Flip back to true to re-enable.
const SHOW_FLAGS = false;

export interface WeeklyGridSection {
  label: string;
  activities: Activity[];
  danger?: boolean;
  getFlag: (a: Activity) => SectionFlag;
}

export interface WeeklyGridProps {
  activities: Activity[];
  tab: 'week' | 'all';
  expandedId: string | null;
  onToggle: (id: string) => void;
  flashId: string | null;
  sectionFlag?: (a: Activity) => SectionFlag;
  userEmail: string;
  projectId: string;
  activityNames: Record<string, string>;
  onSubmitSuccess: (activityId: string, changes: Partial<Activity>, originals: Partial<Activity>) => void;
  onSubmitError?: (message: string) => void;
  onSubmitStart?: () => void;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  sections?: WeeklyGridSection[];
  thresholds?: FloatThresholds;
  // Weekly Lookahead's multi-activity batch save (Part 1) — fired once after a single
  // Save click submits every pending edit across every edited row in one request/CPM run.
  // `summary` carries the before/after CPM comparison the backend now computes (completion
  // date shift, newly-critical activities) so the caller can show a proper summary popup.
  onBatchSubmitSuccess?: (activityIds: string[], updatedActivities: Record<string, any>[], summary: BatchSaveSummary) => void;
}

export interface BatchSaveSummary {
  completion_date: string | null;
  completion_date_before: string | null;
  completion_date_changed: boolean;
  completion_date_days_diff: number;
  newly_critical: { activity_id: string; activity_name: string }[];
  critical_path: { activity_id: string; activity_name: string; is_new: boolean }[];
}

export type EditableWeekField = 'actual_start' | 'actual_finish' | 'forecast_start' | 'forecast_finish';

interface PendingFieldChange {
  field: EditableWeekField;
  oldValue: string;
  newValue: string;
}

type PendingEdits = Record<string, Partial<Record<EditableWeekField, PendingFieldChange>>>;

/* ── Helpers ── */
export function fmtShort(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  const ymd = dateStr.substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt.getTime())) return '—';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${dt.getFullYear()}`;
}

function isAfter(a: string | undefined | null, b: string | undefined | null): boolean {
  if (!a || !b) return false;
  return a.substring(0, 10) > b.substring(0, 10);
}

// Duration fields (target/remain/at-completion _drtn_hr_cnt) are stored in hours — divide
// by 8 to match how Total Float / Free Float are already converted from hours to days
// elsewhere in the app. Shared by all three duration columns in the All Activities tab.
export function durationDays(hrs: number | undefined | null): string {
  if (hrs == null) return '—';
  return String(Math.round(hrs / 8));
}


export function statusBadgeClass(s: string): string {
  const sl = s?.toLowerCase() ?? '';
  if (sl === 'complete' || sl === 'completed') return 'bg-tint text-accent';
  if (sl === 'in progress') return 'bg-watch-bg text-watch-text';
  return 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-300';
}

export function computeStatus(actualStart: string, actualFinish: string): string {
  if (actualFinish) return 'Complete';
  if (actualStart) return 'In Progress';
  return 'Not Started';
}

// Per-activity validation for the Weekly Lookahead inline multi-edit batch — mirrors the
// server's validateActualDates rule (Actual Finish requires Actual Start) plus a client-side
// future-date guard matching ExpandedRow's existing single-edit behavior. Evaluated against
// the EFFECTIVE value for each field (pending edit if present, else the stored value), since
// a PM may edit only one of the two actual-date fields in a given row.
function validateWeekRowEdits(
  effActualStart: string,
  effActualFinish: string,
  todayStr: string,
  editedFields: Set<EditableWeekField>
): string | null {
  if (editedFields.has('actual_start') && effActualStart && effActualStart > todayStr) {
    return 'Actual Start cannot be in the future.';
  }
  if (editedFields.has('actual_finish') && effActualFinish && effActualFinish > todayStr) {
    return 'Actual Finish cannot be in the future.';
  }
  if (effActualFinish && !effActualStart) {
    return 'Actual Finish requires Actual Start.';
  }
  return null;
}

/* ── Inline date-edit cell (Weekly Lookahead grid only) ── */
const DateEditCell: React.FC<{
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  edited?: boolean;
  disabled?: boolean;
  title?: string;
}> = ({ value, onChange, invalid, edited, disabled, title }) => (
  <div className="flex items-center gap-0.5 w-full">
    <input
      type="date"
      value={value}
      disabled={disabled}
      title={title}
      onClick={e => e.stopPropagation()}
      onChange={e => onChange(e.target.value)}
      className={`flex-1 min-w-0 text-xs rounded-[6px] px-1.5 py-1 border tap focus:outline-none focus:ring-1 focus:ring-accent transition-colors ${
        invalid
          ? 'border-crit-text bg-crit-bg text-crit-text'
          : edited
          ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-text dark:text-gray-100 font-semibold'
          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-text dark:text-gray-100'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    />
    {/* One-click clear — native date inputs otherwise require deleting month/day/year
        separately, which is exactly the friction this button removes. */}
    {!!value && !disabled && (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onChange(''); }}
        title="Clear date"
        aria-label="Clear date"
        // 16px on desktop; a full 44px target on touch screens (`tap-sq`, index.css).
        className="shrink-0 w-4 h-4 tap-sq flex items-center justify-center rounded text-gray-400 hover:text-crit-text hover:bg-crit-bg text-sm leading-none transition-colors"
      >
        ×
      </button>
    )}
  </div>
);

/* ── Flag tooltip descriptions ── */
function getFlagTooltip(label: string): string {
  if (label.includes('Not started — due to finish'))
    return 'This activity is scheduled to finish this week but has not started yet. Immediate action needed.';
  if (label.includes('No actual start'))
    return 'The planned start date has passed but no actual start has been recorded.';
  if (label.includes('overdue'))
    return 'This activity started but is past its planned finish date. Each day adds delay.';
  if (label.includes('Behind pace'))
    return 'Progress is too low relative to time elapsed — this activity may not finish on time.';
  return label;
}

const FlagCell: React.FC<{ flag: SectionFlag }> = ({ flag }) => {
  if (!flag) return <span className="text-xs text-gray-300">—</span>;
  const color = flag.color === 'red' ? 'text-crit-text' : 'text-watch-text';
  const tooltip = getFlagTooltip(flag.label);
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`text-xs font-semibold ${color}`}>{flag.label}</span>
      {/* Focusable so a tap (touch has no hover) shows the tip too; tapping elsewhere hides it. */}
      <span
        className="relative group/tip shrink-0 p-1 -m-1 outline-none"
        tabIndex={0}
        role="button"
        aria-label={tooltip}
        onClick={e => e.stopPropagation()}
      >
        <svg className={`w-3 h-3 ${color} opacity-70 cursor-help`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="8.01" strokeWidth={3} strokeLinecap="round" />
          <line x1="12" y1="12" x2="12" y2="16" strokeLinecap="round" />
        </svg>
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-52 text-[11px] leading-snug bg-gray-900 text-white rounded-md px-2.5 py-2 shadow-lg opacity-0 group-hover/tip:opacity-100 group-focus/tip:opacity-100 transition-opacity duration-150 z-50 whitespace-normal text-center">
          {tooltip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      </span>
    </span>
  );
};

/* ── Section header row ── */
const SectionHeaderRow: React.FC<{ label: string; count: number; danger?: boolean; colSpan: number }> = ({
  label, count, danger, colSpan,
}) => (
  <tr>
    <td colSpan={colSpan} className={`py-2 px-3 ${danger ? 'bg-crit-text' : 'bg-ink'}`}>
      <span className="text-xs font-bold text-white uppercase tracking-wider">
        {label} — {count} {count === 1 ? 'activity' : 'activities'}
      </span>
    </td>
  </tr>
);

/* ── Sort icon ── */
const SortIcon: React.FC<{ active: boolean; dir: 'asc' | 'desc' }> = ({ active, dir }) => {
  if (!active) return <span className="ml-1 text-gray-300">↕</span>;
  return <span className="ml-1">{dir === 'asc' ? '▲' : '▼'}</span>;
};

/* ── Activity row ── */
interface ActivityRowProps {
  activity: Activity;
  tab: 'week' | 'all';
  isExpanded: boolean;
  isFlashing: boolean;
  onToggle: () => void;
  userEmail: string;
  projectId: string;
  activityNames: Record<string, string>;
  onSubmitSuccess: (changes: Partial<Activity>, originals: Partial<Activity>) => void;
  onSubmitError?: (message: string) => void;
  onSubmitStart?: () => void;
  sectionFlag: SectionFlag;
  colCount: number;
  thresholds?: FloatThresholds;
  // Weekly Lookahead inline multi-edit (Part 1) — only meaningful/passed when tab==='week'.
  isViewer?: boolean;
  pendingFields?: Partial<Record<EditableWeekField, PendingFieldChange>>;
  rowError?: string | null;
  onFieldEdit?: (field: EditableWeekField, newValue: string) => void;
}

const ActivityRow: React.FC<ActivityRowProps> = ({
  activity, tab, isExpanded, isFlashing, onToggle,
  userEmail, projectId, activityNames, onSubmitSuccess, onSubmitError, onSubmitStart, sectionFlag, colCount, thresholds,
  isViewer, pendingFields, rowError, onFieldEdit,
}) => {
  // Critical tier comes from the backend's is_critical (worst-float / longest-path
  // definition) — the adaptive threshold only decides Watch vs Healthy when an
  // activity isn't on the critical path.
  const floatTier = activity.is_critical ? 'Critical' : getFloatTier(activity.total_float ?? 0, thresholds);
  const floatColor = getFloatColor(floatTier);
  const computedStatus = computeStatus(activity.actual_start ?? '', activity.actual_finish ?? '');

  // Weekly Lookahead no longer supports row expand/click-to-edit (Part 2) — only the All
  // Activities tab still expands via ExpandedRow, so isExpanded/onToggle are meaningful
  // (and only wired up by the parent) for tab==='all'.
  const canExpand = tab === 'all';
  const canEditInline = tab === 'week' && !!onFieldEdit && !isViewer;

  const getFieldVal = (field: EditableWeekField, stored: string | undefined | null): string =>
    pendingFields?.[field]?.newValue ?? formatDateInput(stored);
  const effActualStart = getFieldVal('actual_start', activity.actual_start);
  const effActualFinish = getFieldVal('actual_finish', activity.actual_finish);

  // At least one field on this row has an unsaved pending edit — highlighted so it's
  // findable at a glance across a whole page of rows while editing several at once.
  const isRowEdited = canEditInline && !!pendingFields && Object.keys(pendingFields).length > 0;

  const stickyBg = isExpanded
    ? 'bg-tint dark:bg-green-900/10'
    : isFlashing
    ? 'bg-green-100 dark:bg-green-900/20'
    : isRowEdited
    ? 'bg-indigo-50 dark:bg-indigo-900/20'
    : 'bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800/40';

  const rowClass = `group border-b border-gray-100 dark:border-gray-800 divide-x divide-gray-100 dark:divide-gray-700 transition-colors ${
    canExpand ? 'cursor-pointer' : ''
  } ${isFlashing ? 'ring-2 ring-green-500' : ''} ${
    isExpanded
      ? 'bg-tint dark:bg-green-900/10'
      : isRowEdited
      ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-l-indigo-400'
      : canExpand ? 'hover:bg-gray-50 dark:hover:bg-gray-800/40' : ''
  }`;

  return (
    <>
      <tr onClick={canExpand ? onToggle : undefined} className={rowClass}>
        {tab === 'all' ? (
          <>
            {/* Activity ID — sticky left */}
            <td className={`sticky left-0 z-10 py-2 px-2 transition-colors w-[92px] sm:w-[160px] ${stickyBg}`}>
              <div className="flex items-center gap-1.5">
                <svg
                  className={`w-3 h-3 shrink-0 text-muted transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <p className="text-xs sm:text-sm font-mono text-muted dark:text-gray-400 break-all">{activity.activity_id}</p>
              </div>
            </td>

            {/* Activity Name */}
            <td className={`sticky left-[92px] sm:left-[160px] z-10 py-2 px-2 transition-colors w-[150px] sm:w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${stickyBg}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="text-sm font-semibold text-text dark:text-gray-100 leading-snug whitespace-normal break-words min-w-0">{activity.activity_name}</p>
                {/* Risk badge is float-driven, but a finished activity isn't "at risk"
                    regardless of what its float value says — only flag work still underway. */}
                {computedStatus !== 'Complete' && (
                  <span
                    style={{ backgroundColor: floatColor.bg, color: floatColor.text }}
                    className="shrink-0 pill"
                  >
                    {getFloatBadgeText(floatTier)}
                  </span>
                )}
              </div>
            </td>
          </>
        ) : (
          <>
            {/* Activity cell — sticky left (This week tab keeps the combined ID+name cell).
                No expand chevron here — Weekly Lookahead no longer supports row expand. */}
            <td className={`sticky left-0 z-10 py-2 px-2 transition-colors w-[160px] sm:w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${stickyBg}`}>
              <div className="flex-1 min-w-0 flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text dark:text-gray-100 leading-snug">{activity.activity_name}</p>
                  <p className="text-[11px] font-mono text-muted dark:text-gray-400 mt-0.5">{activity.activity_id}</p>
                </div>
                {/* Same status gate as the All Activities tab — a finished activity
                    isn't "at risk" regardless of its float value. */}
                {computedStatus !== 'Complete' && (
                  <span
                    style={{ backgroundColor: floatColor.bg, color: floatColor.text }}
                    className="shrink-0 pill"
                  >
                    {getFloatBadgeText(floatTier)}
                  </span>
                )}
              </div>
            </td>
          </>
        )}

        {/* Start — hidden mobile */}
        <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[110px]">
          {fmtShort(activity.start_date)}
        </td>

        {/* Finish */}
        <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[110px]">
          {fmtShort(activity.finish_date)}
        </td>

        {/* Actual start — hidden mobile */}
        <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[110px]">
          {canEditInline ? (
            <DateEditCell
              value={effActualStart}
              onChange={v => onFieldEdit!('actual_start', v)}
              invalid={!!rowError}
              edited={!!pendingFields?.actual_start}
            />
          ) : fmtShort(activity.actual_start)}
        </td>

        {/* Actual finish — red if late (read-only) / red if invalid (editable) */}
        <td className={`text-center py-2.5 px-2 text-xs whitespace-nowrap w-[110px] ${
          !canEditInline && isAfter(activity.actual_finish, activity.planned_finish)
            ? 'text-crit-text font-semibold'
            : 'text-muted dark:text-gray-400'
        }`}>
          {canEditInline ? (
            <DateEditCell
              value={effActualFinish}
              onChange={v => onFieldEdit!('actual_finish', v)}
              invalid={!!rowError}
              edited={!!pendingFields?.actual_finish}
            />
          ) : fmtShort(activity.actual_finish)}
        </td>

        {/* Tab-specific columns */}
        {tab === 'week' ? (
          <>
            {/* Forecast start — hidden mobile */}
            <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[110px]">
              {canEditInline ? (
                <DateEditCell
                  value={getFieldVal('forecast_start', activity.forecast_start)}
                  onChange={v => onFieldEdit!('forecast_start', v)}
                  disabled={!!effActualStart}
                  title={effActualStart ? 'Not needed — actual start is recorded' : undefined}
                  edited={!!pendingFields?.forecast_start}
                />
              ) : fmtShort(activity.forecast_start)}
            </td>

            {/* Forecast finish — hidden mobile, red if late (read-only) */}
            <td className={`text-center py-2.5 px-2 text-xs whitespace-nowrap w-[110px] ${
              !canEditInline && isAfter(activity.forecast_finish, activity.planned_finish)
                ? 'text-crit-text font-semibold'
                : 'text-muted dark:text-gray-400'
            }`}>
              {canEditInline ? (
                <DateEditCell
                  value={getFieldVal('forecast_finish', activity.forecast_finish)}
                  onChange={v => onFieldEdit!('forecast_finish', v)}
                  disabled={!!effActualFinish}
                  title={effActualFinish ? 'Not needed — actual finish is recorded' : undefined}
                  edited={!!pendingFields?.forecast_finish}
                />
              ) : fmtShort(activity.forecast_finish)}
            </td>
          </>
        ) : (
          <>
            {/* Original Duration (days) — All activities tab */}
            <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[90px]">
              {durationDays(activity.target_drtn_hr_cnt)}
            </td>

            {/* Remaining Duration (days) — All activities tab */}
            <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[90px]">
              {durationDays(activity.remain_drtn_hr_cnt)}
            </td>

            {/* At Completion Duration (days) — All activities tab */}
            <td className="text-center py-2.5 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap w-[90px]">
              {durationDays(activity.at_complete_drtn_hr_cnt)}
            </td>
          </>
        )}

        {/* Float */}
        <td
          className="text-center py-2.5 px-2 text-xs w-[70px] font-semibold"
          style={{ color: floatColor.text }}
        >
          {activity.total_float != null ? activity.total_float : '—'}
        </td>

        {/* Status badge */}
        <td className="text-center py-2.5 px-2 w-[90px]">
          <span className={`pill ${statusBadgeClass(computedStatus)}`}>
            {computedStatus}
          </span>
        </td>

        {/* Flags (This week tab only) */}
        {tab === 'week' && SHOW_FLAGS && (
          <td className="py-2.5 px-2 w-[130px]">
            <FlagCell flag={sectionFlag} />
          </td>
        )}
      </tr>

      {/* Row-level validation message (Weekly Lookahead inline edit only) */}
      {tab === 'week' && rowError && (
        <tr>
          <td colSpan={colCount} className="py-1 px-3 bg-crit-bg text-[11px] text-crit-text font-medium">
            {activity.activity_id}: {rowError}
          </td>
        </tr>
      )}

      {/* Expanded row — All Activities tab only. Weekly Lookahead never expands a row;
          it only supports the inline multi-edit + single Save flow above. */}
      {tab === 'all' && isExpanded && (
        <ExpandedRow
          activity={activity}
          tab={tab}
          colSpan={colCount}
          userEmail={userEmail}
          projectId={projectId}
          activityNames={activityNames}
          onSubmitSuccess={onSubmitSuccess}
          onSubmitError={onSubmitError}
          onSubmitStart={onSubmitStart}
          onCancel={onToggle}
        />
      )}
    </>
  );
};

/* ── Phone layout (<768px): one card per activity instead of the wide table ── */
const PHONE_QUERY = '(max-width: 767px)';

function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

// Group header on phones — sticks just below the app header while its cards scroll by.
const SectionHeading: React.FC<{ label: string; count: number; danger?: boolean }> = ({ label, count, danger }) => (
  <h3
    className={`sticky z-10 py-2 px-3 rounded-[8px] text-xs font-bold text-white uppercase tracking-wider ${danger ? 'bg-crit-text' : 'bg-ink'}`}
    style={{ top: 'var(--app-header-h, 60px)' }}
  >
    {label} — {count} {count === 1 ? 'activity' : 'activities'}
  </h3>
);

const ActivityCard: React.FC<ActivityRowProps> = ({
  activity, tab, isExpanded, isFlashing, onToggle,
  userEmail, projectId, activityNames, onSubmitSuccess, onSubmitError, onSubmitStart, sectionFlag, thresholds,
  isViewer, pendingFields, rowError, onFieldEdit,
}) => {
  // Same derivations as ActivityRow — only the layout differs.
  const floatTier = activity.is_critical ? 'Critical' : getFloatTier(activity.total_float ?? 0, thresholds);
  const floatColor = getFloatColor(floatTier);
  const computedStatus = computeStatus(activity.actual_start ?? '', activity.actual_finish ?? '');
  const canExpand = tab === 'all';
  const canEditInline = tab === 'week' && !!onFieldEdit && !isViewer;
  const getFieldVal = (field: EditableWeekField, stored: string | undefined | null): string =>
    pendingFields?.[field]?.newValue ?? formatDateInput(stored);
  const effActualStart = getFieldVal('actual_start', activity.actual_start);
  const effActualFinish = getFieldVal('actual_finish', activity.actual_finish);
  const isRowEdited = canEditInline && !!pendingFields && Object.keys(pendingFields).length > 0;

  const dateField = (label: string, field: EditableWeekField, stored: string | undefined | null, opts: {
    disabled?: boolean; title?: string; invalid?: boolean; late?: boolean;
  } = {}) => (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted mb-1">{label}</div>
      {canEditInline ? (
        <DateEditCell
          value={getFieldVal(field, stored)}
          onChange={v => onFieldEdit!(field, v)}
          disabled={opts.disabled}
          title={opts.title}
          invalid={opts.invalid}
          edited={!!pendingFields?.[field]}
        />
      ) : (
        <div className={`text-sm ${opts.late ? 'text-crit-text font-semibold' : 'text-text'}`}>{fmtShort(stored)}</div>
      )}
    </div>
  );

  const stat = (label: string, value: React.ReactNode, style?: React.CSSProperties) => (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted truncate" title={label}>{label}</div>
      <div className="text-sm font-semibold text-text mt-0.5" style={style}>{value}</div>
    </div>
  );

  return (
    <article
      className={`border rounded-[12px] p-3 space-y-3 ${
        isRowEdited ? 'border-indigo-400 bg-indigo-50' : isExpanded ? 'border-accent bg-white' : 'border-line bg-white'
      } ${isFlashing ? 'ring-2 ring-green-500' : ''}`}
    >
      <div
        className={`flex items-start justify-between gap-2 ${canExpand ? 'cursor-pointer' : ''}`}
        onClick={canExpand ? onToggle : undefined}
        role={canExpand ? 'button' : undefined}
        aria-expanded={canExpand ? isExpanded : undefined}
      >
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-text leading-snug break-anywhere">{activity.activity_name}</p>
          <p className="text-[11px] font-mono text-muted mt-0.5 break-anywhere">{activity.activity_id}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`pill ${statusBadgeClass(computedStatus)}`}>{computedStatus}</span>
          {computedStatus !== 'Complete' && (
            <span style={{ backgroundColor: floatColor.bg, color: floatColor.text }} className="pill">
              {getFloatBadgeText(floatTier)}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 border-t border-line pt-2">
        {stat('Start', fmtShort(activity.start_date))}
        {stat('Finish', fmtShort(activity.finish_date))}
        {stat('Float', activity.total_float != null ? `${activity.total_float}d` : '—', { color: floatColor.text })}
      </div>

      {tab === 'week' ? (
        <div className="space-y-2">
          {dateField('Actual start', 'actual_start', activity.actual_start, { invalid: !!rowError })}
          {dateField('Actual finish', 'actual_finish', activity.actual_finish, {
            invalid: !!rowError, late: isAfter(activity.actual_finish, activity.planned_finish),
          })}
          {dateField('Forecast start', 'forecast_start', activity.forecast_start, {
            disabled: !!effActualStart, title: effActualStart ? 'Not needed — actual start is recorded' : undefined,
          })}
          {dateField('Forecast finish', 'forecast_finish', activity.forecast_finish, {
            disabled: !!effActualFinish, title: effActualFinish ? 'Not needed — actual finish is recorded' : undefined,
            late: isAfter(activity.forecast_finish, activity.planned_finish),
          })}
          {SHOW_FLAGS && sectionFlag && <FlagCell flag={sectionFlag} />}
          {rowError && <p className="text-[12px] text-crit-text font-medium">{rowError}</p>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {stat('Actual start', fmtShort(activity.actual_start))}
            {stat('Actual finish', fmtShort(activity.actual_finish),
              isAfter(activity.actual_finish, activity.planned_finish) ? { color: 'var(--crit-text)' } : undefined)}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {stat('Original duration', durationDays(activity.target_drtn_hr_cnt))}
            {stat('Remaining duration', durationDays(activity.remain_drtn_hr_cnt))}
            {stat('At completion', durationDays(activity.at_complete_drtn_hr_cnt))}
          </div>
          {isExpanded && (
            // ExpandedRow renders a table row; a one-cell table hosts it unchanged.
            <table className="w-full table-fixed">
              <tbody>
                <ExpandedRow
                  activity={activity}
                  tab={tab}
                  colSpan={1}
                  userEmail={userEmail}
                  projectId={projectId}
                  activityNames={activityNames}
                  onSubmitSuccess={onSubmitSuccess}
                  onSubmitError={onSubmitError}
                  onSubmitStart={onSubmitStart}
                  onCancel={onToggle}
                />
              </tbody>
            </table>
          )}
        </>
      )}
    </article>
  );
};

/* ── Main grid ── */
const WeeklyGrid: React.FC<WeeklyGridProps> = ({
  activities,
  tab,
  expandedId,
  onToggle,
  flashId,
  userEmail,
  projectId,
  activityNames,
  onSubmitSuccess,
  onSubmitError,
  onSubmitStart,
  sortKey,
  sortDir,
  onSort,
  sections,
  thresholds,
  onBatchSubmitSuccess,
}) => {
  const flowClient = useFlowClient();
  const { projects } = useApp();
  const project = projects.find(p => p.id === projectId);
  const isViewer = project?.role_on_project === 'Viewer';
  // The Lookahead grid's own inline multi-edit "Save all" flow (approveMultiBatch) is a
  // separate code path from ExpandedRow's per-activity submit — same shared guard/modal
  // pattern applied here too, so a demo visitor sees the same message from both.
  const { gateOpen, gateFeature, closeGate, guard } = useDemoGuard();
  const isPhone = useIsPhone();

  // ── Weekly Lookahead inline multi-edit state (Part 1) ──────────────────────
  const [pendingEdits, setPendingEdits] = useState<PendingEdits>({});
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchSaveError, setBatchSaveError] = useState<string | null>(null);

  const weekActivityById = useMemo(() => {
    const map = new Map<string, Activity>();
    if (tab === 'week' && sections) {
      for (const section of sections) {
        for (const a of section.activities) map.set(a.activity_id, a);
      }
    }
    return map;
  }, [tab, sections]);

  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const handleFieldEdit = useCallback((activityId: string, field: EditableWeekField, newValue: string) => {
    const activity = weekActivityById.get(activityId);
    if (!activity) return;
    const origVal = formatDateInput((activity as unknown as Record<string, unknown>)[field] as string | null | undefined);
    setPendingEdits(prev => {
      const next = { ...prev };
      const activityEdits = { ...(next[activityId] ?? {}) };
      if (newValue === origVal) {
        delete activityEdits[field];
      } else {
        activityEdits[field] = { field, oldValue: origVal, newValue };
      }
      if (Object.keys(activityEdits).length === 0) {
        delete next[activityId];
      } else {
        next[activityId] = activityEdits;
      }
      return next;
    });
    setBatchSaveError(null);
  }, [weekActivityById]);

  const rowErrors = useMemo(() => {
    const errors = new Map<string, string>();
    for (const [activityId, fields] of Object.entries(pendingEdits)) {
      const activity = weekActivityById.get(activityId);
      if (!activity) continue;
      const effActualStart = fields.actual_start?.newValue ?? formatDateInput(activity.actual_start);
      const effActualFinish = fields.actual_finish?.newValue ?? formatDateInput(activity.actual_finish);
      const editedFields = new Set(Object.keys(fields) as EditableWeekField[]);
      const err = validateWeekRowEdits(effActualStart, effActualFinish, todayStr, editedFields);
      if (err) errors.set(activityId, err);
    }
    return errors;
  }, [pendingEdits, weekActivityById, todayStr]);

  const editedActivityCount = Object.keys(pendingEdits).length;
  const hasPendingEdits = editedActivityCount > 0;
  const canSaveBatch = hasPendingEdits && rowErrors.size === 0 && !batchSaving && !isViewer;

  const handleDiscardAll = useCallback(() => {
    setPendingEdits({});
    setBatchSaveError(null);
  }, []);

  const handleSaveAll = useCallback(async () => {
    const entries = Object.entries(pendingEdits);
    if (!entries.length) return;
    setBatchSaving(true);
    setBatchSaveError(null);
    onSubmitStart?.();
    try {
      const activitiesPayload = entries.map(([activityId, fields]) => {
        const activity = weekActivityById.get(activityId);
        const changes: { field_name: string; new_value: string; old_value: string }[] = Object.values(fields).map(fc => ({
          field_name: fc.field, new_value: fc.newValue, old_value: fc.oldValue,
        }));
        // Mirror ExpandedRow's client-side status recompute — the server recomputes
        // authoritatively too, but sending it keeps the STAGING audit trail consistent
        // with the single-edit flow.
        if (activity && (fields.actual_start || fields.actual_finish)) {
          const effActualStart = fields.actual_start?.newValue ?? formatDateInput(activity.actual_start);
          const effActualFinish = fields.actual_finish?.newValue ?? formatDateInput(activity.actual_finish);
          const newStatus = computeStatus(effActualStart, effActualFinish);
          changes.push({ field_name: 'activity_status', new_value: newStatus, old_value: String(activity.status ?? '') });
        }
        return { activity_id: activityId, changes };
      });

      const result = await flowClient.approveMultiBatch(userEmail, projectId, activitiesPayload, 'Low', 'Manual multi-activity update');
      onBatchSubmitSuccess?.(entries.map(([id]) => id), result.updatedActivities ?? [], {
        completion_date: result.completion_date,
        completion_date_before: result.completion_date_before,
        completion_date_changed: result.completion_date_changed,
        completion_date_days_diff: result.completion_date_days_diff,
        newly_critical: result.newly_critical ?? [],
        critical_path: result.critical_path ?? [],
      });
      setPendingEdits({});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Batch save failed. Please try again.';
      setBatchSaveError(message);
      onSubmitError?.(message);
    } finally {
      setBatchSaving(false);
    }
  }, [pendingEdits, weekActivityById, flowClient, userEmail, projectId, onBatchSubmitSuccess, onSubmitError, onSubmitStart]);

  // This week tab: 9 cols. All activities tab: 11 cols (no % done).
  const WEEK_COLS = SHOW_FLAGS ? 10 : 9;  // Activity, Start, Finish, Actual start, Actual finish, Forecast start, Forecast finish, Float, Status[, Flags]
  const ALL_COLS = 11;  // Activity ID, Activity Name, Start, Finish, Actual start, Actual finish, Original Duration, Remaining Duration, At Completion Duration, Float, Status
  const colCount = tab === 'week' ? WEEK_COLS : ALL_COLS;

  const SortableTh: React.FC<{ label: string; colKey: string; className?: string }> = ({ label, colKey, className }) => {
    const isActive = sortKey === colKey;
    return (
      <th
        className={`th-flo text-center py-2.5 px-2 dark:bg-gray-800 select-none ${onSort ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''} ${className ?? ''}`}
        onClick={() => onSort?.(colKey)}
      >
        {label}
        {onSort && <SortIcon active={isActive} dir={sortDir ?? 'asc'} />}
      </th>
    );
  };

  const weekHeaders = (
    <tr className="divide-x divide-gray-100 dark:divide-gray-700">
      <th className="th-flo sticky left-0 z-30 dark:bg-gray-800 text-left py-2.5 px-3 w-[160px] sm:w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">
        Activity
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
        Start
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
        Finish
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
        Actual start
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
        Actual finish
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
        Forecast start
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
        Forecast finish
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[55px]">
        Float (Days)
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[90px]">
        Status
      </th>
      {SHOW_FLAGS && (
        <th className="th-flo text-left py-2.5 px-2 dark:bg-gray-800 w-[130px]">
          Flags
        </th>
      )}
    </tr>
  );

  const allHeaders = (
    <tr className="divide-x divide-gray-100 dark:divide-gray-700">
      <th className="th-flo sticky left-0 z-30 dark:bg-gray-800 text-left py-2.5 px-2 sm:px-3 w-[92px] sm:w-[160px]">
        Activity ID
      </th>
      <th className="th-flo sticky left-[92px] sm:left-[160px] z-30 text-left py-2.5 px-2 dark:bg-gray-800 w-[150px] sm:w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">
        Activity Name
      </th>
      <SortableTh label="Start" colKey="start_date" className="w-[110px]" />
      <SortableTh label="Finish" colKey="finish_date" className="w-[110px]" />
      <SortableTh label="Actual start" colKey="actual_start" className="w-[110px]" />
      <SortableTh label="Actual finish" colKey="actual_finish" className="w-[110px]" />
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[90px]">
        Original Duration (Days)
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[90px]">
        Remaining Duration (Days)
      </th>
      <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[90px]">
        At Completion Duration (Days)
      </th>
      <SortableTh label="Float (Days)" colKey="total_float" className="w-[70px]" />
      <SortableTh label="Status" colKey="status" className="w-[90px]" />
    </tr>
  );

  return (
    <>
      {isPhone ? (
        <div className="space-y-3 py-3">
          {tab === 'week' && sections ? (
            sections.map((section) => {
              if (!section.activities.length) return null;
              return (
                <section key={section.label} className="space-y-2">
                  <SectionHeading label={section.label} count={section.activities.length} danger={section.danger} />
                  {section.activities.map(activity => (
                    <ActivityCard
                      key={activity.activity_id}
                      activity={activity}
                      tab={tab}
                      isExpanded={false}
                      isFlashing={flashId === activity.activity_id}
                      onToggle={() => onToggle(activity.activity_id)}
                      userEmail={userEmail}
                      projectId={projectId}
                      activityNames={activityNames}
                      onSubmitSuccess={(changes, originals) => onSubmitSuccess(activity.activity_id, changes, originals)}
                      onSubmitError={onSubmitError}
                      onSubmitStart={onSubmitStart}
                      sectionFlag={section.getFlag(activity)}
                      colCount={colCount}
                      thresholds={thresholds}
                      isViewer={isViewer}
                      pendingFields={pendingEdits[activity.activity_id]}
                      rowError={rowErrors.get(activity.activity_id) ?? null}
                      onFieldEdit={(field, value) => handleFieldEdit(activity.activity_id, field, value)}
                    />
                  ))}
                </section>
              );
            })
          ) : (
            activities.map(activity => (
              <ActivityCard
                key={activity.activity_id}
                activity={activity}
                tab={tab}
                isExpanded={expandedId === activity.activity_id}
                isFlashing={flashId === activity.activity_id}
                onToggle={() => onToggle(activity.activity_id)}
                userEmail={userEmail}
                projectId={projectId}
                activityNames={activityNames}
                onSubmitSuccess={(changes, originals) => onSubmitSuccess(activity.activity_id, changes, originals)}
                onSubmitError={onSubmitError}
                onSubmitStart={onSubmitStart}
                sectionFlag={null}
                colCount={colCount}
                thresholds={thresholds}
              />
            ))
          )}
          {/* Room for the fixed Save bar so the last card isn't trapped under it. */}
          {hasPendingEdits && <div className="h-36" aria-hidden="true" />}
        </div>
      ) : (
      <ScrollFade>
      <div className="overflow-auto overscroll-x-contain" style={{ maxHeight: hasPendingEdits ? 'calc(100dvh - 260px)' : 'calc(100dvh - 200px)' }}>
        <table className="border-collapse bg-white dark:bg-gray-900 w-full min-w-[860px]" style={{ tableLayout: 'fixed' }}>
          <thead className="sticky top-0 z-20 border-b-2 border-gray-200 dark:border-gray-700">
            {tab === 'week' ? weekHeaders : allHeaders}
          </thead>
          <tbody>
            {tab === 'week' && sections ? (
              sections.map((section) => {
                if (!section.activities.length) return null;
                return (
                  <React.Fragment key={section.label}>
                    <SectionHeaderRow
                      label={section.label}
                      count={section.activities.length}
                      danger={section.danger}
                      colSpan={colCount}
                    />
                    {section.activities.map(activity => (
                      <ActivityRow
                        key={activity.activity_id}
                        activity={activity}
                        tab={tab}
                        isExpanded={expandedId === activity.activity_id}
                        isFlashing={flashId === activity.activity_id}
                        onToggle={() => onToggle(activity.activity_id)}
                        userEmail={userEmail}
                        projectId={projectId}
                        activityNames={activityNames}
                        onSubmitSuccess={(changes, originals) => onSubmitSuccess(activity.activity_id, changes, originals)}
                        onSubmitError={onSubmitError}
                        onSubmitStart={onSubmitStart}
                        sectionFlag={section.getFlag(activity)}
                        colCount={colCount}
                        thresholds={thresholds}
                        isViewer={isViewer}
                        pendingFields={pendingEdits[activity.activity_id]}
                        rowError={rowErrors.get(activity.activity_id) ?? null}
                        onFieldEdit={(field, value) => handleFieldEdit(activity.activity_id, field, value)}
                      />
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              activities.map(activity => (
                <ActivityRow
                  key={activity.activity_id}
                  activity={activity}
                  tab={tab}
                  isExpanded={expandedId === activity.activity_id}
                  isFlashing={flashId === activity.activity_id}
                  onToggle={() => onToggle(activity.activity_id)}
                  userEmail={userEmail}
                  projectId={projectId}
                  activityNames={activityNames}
                  onSubmitSuccess={(changes, originals) => onSubmitSuccess(activity.activity_id, changes, originals)}
                  onSubmitError={onSubmitError}
                  onSubmitStart={onSubmitStart}
                  sectionFlag={null}
                  colCount={colCount}
                  thresholds={thresholds}
                />
              ))
            )}
          </tbody>
        </table>
        {/* Spacer reserving room for the fixed Save bar below, INSIDE this same scroll
            container — a `position: fixed` bar doesn't participate in document flow, so
            without this the last row(s) end up permanently covered by it with nowhere
            left to scroll to reveal them. */}
        {hasPendingEdits && <div className="h-36 sm:h-20" aria-hidden="true" />}
      </div>
      </ScrollFade>
      )}

      {/* Single Save bar for the whole batch of pending edits across every edited row
          (Part 1) — only ever shown on Weekly Lookahead, once at least one field has
          been edited, and never for Viewers. */}
      {tab === 'week' && !isViewer && hasPendingEdits && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-t-2 border-accent shadow-2xl px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex flex-wrap items-center gap-x-3 gap-y-2 safe-x">
          <span className="text-sm font-semibold text-text dark:text-gray-100">
            {editedActivityCount} {editedActivityCount === 1 ? 'activity' : 'activities'} edited
          </span>
          {rowErrors.size > 0 && (
            <span className="text-xs text-crit-text font-medium">
              Fix {rowErrors.size} invalid {rowErrors.size === 1 ? 'activity' : 'activities'} before saving.
            </span>
          )}
          {batchSaveError && (
            <span className="text-xs text-crit-text font-medium">{batchSaveError}</span>
          )}
          <div className="w-full sm:w-auto sm:ml-auto flex gap-2">
            <button
              onClick={handleDiscardAll}
              disabled={batchSaving}
              className="flex-1 sm:flex-none border border-gray-300 dark:border-gray-600 text-muted dark:text-gray-400 font-semibold px-4 py-2 rounded-[8px] text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 transition-colors tap"
            >
              Discard
            </button>
            <button
              onClick={() => guard(handleSaveAll, 'save schedule updates')}
              disabled={!canSaveBatch}
              className="flex-[2] sm:flex-none bg-accent hover:bg-link text-white font-semibold px-5 py-2 rounded-[8px] text-sm disabled:opacity-50 transition-colors sm:min-w-[140px] tap"
            >
              {batchSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : `Save ${editedActivityCount} ${editedActivityCount === 1 ? 'activity' : 'activities'}`}
            </button>
          </div>
        </div>
      )}
      <DemoSignInModal open={gateOpen} onClose={closeGate} feature={gateFeature} />
    </>
  );
};

export default WeeklyGrid;
